import { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import Header from '../components/blog/Header';
import HeroSection from '../components/blog/HeroSection';
import PostCard from '../components/blog/PostCard';
import CategoryNav from '../components/blog/CategoryNav';
import Newsletter from '../components/blog/Newsletter';
import Footer from '../components/blog/Footer';
import PopularCategories from '../components/blog/PopularCategories';
import { Container } from '../components/ui/Container';
import Pagination from '../components/ui/Pagination';
import AdContainer from '../components/ads/AdContainer';
import Spinner from '../components/ui/Spinner';
import { usePagination } from '../hooks/usePagination';
import SEO from '../components/seo/SEO';
import StructuredData, { generateOrganizationSchema, generateWebSiteSchema } from '../components/seo/StructuredData';
import { useRegionalSeo } from '../hooks/useRegionalSeo';
import { useSeoExtras } from '../context/SiteSeoContext';
import { fetchTopArticles, fetchPopularArticles, fetchArticles } from '../store/actions/articleActions';
import { fetchPopularCategories, fetchCategories } from '../store/actions/categoryActions';
import { transformArticles, transformCategories } from '../utils/transformData';

const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background};
`;

const Main = styled.main`
  ${Container} {
    padding-top: 2rem;
    padding-bottom: 2rem;
    
    @media (min-width: 768px) {
      padding-top: 3rem;
      padding-bottom: 3rem;
    }
    
    @media (min-width: 1024px) {
      padding-top: 4rem;
      padding-bottom: 4rem;
    }
    
    @media (min-width: 1440px) {
      padding-top: 5rem;
      padding-bottom: 5rem;
    }
  }
`;

const HeroSectionWrapper = styled.section`
  margin-bottom: 3rem;
  
  @media (min-width: 768px) {
    margin-bottom: 4rem;
  }
`;

const HeroHeader = styled.div`
  margin-bottom: 2rem;
  
  @media (min-width: 768px) {
    margin-bottom: 2.5rem;
  }
`;

const HeroTitle = styled.h1`
  font-family: ${props => props.theme.typography.fonts.display};
  font-size: 1.875rem;
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  line-height: 1.2;
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.foreground};
  
  @media (min-width: 768px) {
    font-size: 2.5rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 3rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 3.5rem;
  }
  
  @media (min-width: 1920px) {
    font-size: 4rem;
  }
`;

const HeroDescription = styled.p`
  font-size: 1rem;
  color: ${props => props.theme.colors.mutedForeground};
  max-width: 42rem;
  line-height: 1.7;
  
  @media (min-width: 768px) {
    font-size: 1.125rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 1.25rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 1.375rem;
  }
`;

const ArticlesSection = styled.section`
  margin-bottom: 2rem;
  
  @media (min-width: 768px) {
    margin-bottom: 2.5rem;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.typography.fonts.display};
  font-size: 1.5rem;
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  
  @media (min-width: 768px) {
    font-size: 1.75rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 2rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 2.25rem;
  }
`;

const PostsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
  margin-bottom: 3rem;
  
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1.5rem;
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1.75rem;
    margin-bottom: 4rem;
  }
  
  @media (min-width: 1440px) {
    gap: 2rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 0;
  
  p {
    color: ${props => props.theme.colors.mutedForeground};
  }
`;

const Index = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { language, apiRegion } = useLanguage();
  const [activeCategory, setActiveCategory] = useState('all');
  const [currentPage] = useState(1);
  
  // Use language from context (set based on region from URL)
  const activeLanguage = language;

  // Redux selectors
  const { 
    articles, 
    topArticles, 
    popularArticles: popularArticlesFromStore, 
    loading: articlesLoading 
  } = useSelector(state => state.article);
  
  const { 
    categories: categoriesFromStore, 
    popularCategories: popularCategoriesFromStore,
    loading: categoriesLoading 
  } = useSelector(state => state.category);

  // Fetch data on mount and when language changes
  useEffect(() => {
    const localeParams = { lang: activeLanguage, region: apiRegion };
    const fetchParams = {
      page: currentPage,
      limit: 10,
      ...localeParams,
    };
    
    dispatch(fetchTopArticles(5, localeParams));
    dispatch(fetchPopularArticles(10, localeParams));
    dispatch(fetchPopularCategories(4, localeParams));
    dispatch(fetchCategories(localeParams));
    dispatch(fetchArticles(fetchParams));
  }, [dispatch, currentPage, activeLanguage, apiRegion]);

  // Transform data
  const transformedArticles = useMemo(() => transformArticles(articles), [articles]);
  const transformedTopArticles = useMemo(() => transformArticles(topArticles), [topArticles]);
  const transformedPopularArticles = useMemo(() => transformArticles(popularArticlesFromStore), [popularArticlesFromStore]);
  const transformedCategories = useMemo(() => transformCategories(categoriesFromStore), [categoriesFromStore]);
  const transformedPopularCategories = useMemo(() => transformCategories(popularCategoriesFromStore), [popularCategoriesFromStore]);

  // Filter articles by category
  const filteredPosts = useMemo(() => {
    if (activeCategory === 'all') {
      return transformedArticles;
    }
    return transformedArticles.filter(post => 
      post.category?.toLowerCase() === activeCategory.toLowerCase() ||
      post.category === activeCategory
    );
  }, [transformedArticles, activeCategory]);

  const { paginatedItems, totalPages, goToPage } = usePagination(filteredPosts, 6);

  // Get top 5 articles for carousel
  const top5Articles = transformedTopArticles.slice(0, 5);
  
  // Get popular articles for sidebar (skip first 5 if they're in top articles)
  const popularArticles = transformedPopularArticles.slice(0, 10);

  const loading = articlesLoading || categoriesLoading;

  const regionalSeo = useRegionalSeo('/');
  const seoExtras = useSeoExtras();

  return (
    <PageWrapper>
      <SEO
        title={t('nav.home')}
        description={t('site.description')}
        url={regionalSeo.pageUrl}
        language={regionalSeo.language}
        availableLanguages={regionalSeo.availableLanguages}
        alternateUrls={regionalSeo.alternateUrls}
        {...seoExtras}
      />
      <StructuredData data={generateOrganizationSchema(t('site.description'))} />
      <StructuredData data={generateWebSiteSchema(t('site.description'))} />
      <Header />
      
      <Main>
        <Container>
          <HeroSectionWrapper aria-labelledby="hero-heading">
            <HeroHeader>
              <HeroTitle id="hero-heading">{t('hero.title')}</HeroTitle>
              <HeroDescription>
                {t('hero.description')}
              </HeroDescription>
            </HeroHeader>
            
            {loading ? (
              <Spinner />
            ) : (
              <>
                <HeroSection topArticles={top5Articles} popularArticles={popularArticles} />
              </>
            )}
          </HeroSectionWrapper>

          {!categoriesLoading && (
            <PopularCategories categories={transformedPopularCategories} />
          )}

          <ArticlesSection aria-labelledby="articles-heading">
            <SectionHeader>
              <SectionTitle id="articles-heading">{t('section.latestArticles')}</SectionTitle>
            </SectionHeader>
            {!categoriesLoading && (
              <CategoryNav
                categories={transformedCategories}
                activeCategory={activeCategory}
                onCategoryChange={setActiveCategory}
              />
            )}
          </ArticlesSection>

          {loading ? (
            <Spinner />
          ) : (
            <>
              <PostsGrid aria-label="Blog posts">
                {paginatedItems.map((post, index) => (
                  <>
                    <PostCard
                      key={post._id || `${post.title}-${index}`}
                      {...post}
                      index={index}
                    />
                    {/* Show ad after every 3rd article */}
                    {(index + 1) % 3 === 0 && index < paginatedItems.length - 1 && (
                      <AdContainer
                        key={`ad-${index}`}
                        placement="between_articles"
                        limit={1}
                        style={{ gridColumn: '1 / -1' }}
                      />
                    )}
                  </>
                ))}
              </PostsGrid>
            </>
          )}

          {filteredPosts.length === 0 && (
            <EmptyState>
              <p>No posts found in this category.</p>
            </EmptyState>
          )}

          {filteredPosts.length > 0 && totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={goToPage}
            />
          )}

          <Newsletter />
        </Container>
      </Main>

      <Footer />
    </PageWrapper>
  );
};

export default Index;


